/**
 * Distill a discussion into reviewable proposal cards (G8): the goal, the plan (a workstream
 * brief), tasks, and the agent's still-open proposals. Built on the device from the sealed
 * conversation — the agent is asked (triage preamble) to state outcomes as "Goal: …", "Plan: …"
 * and "Task: …" lines, which are parsed deterministically here; nothing runs until the user
 * accepts a card, and accepting performs exactly the change the card shows.
 */
import type { BarkRecord, DistillChange, DistillProposal } from '@viko/protocol'

export interface DistillContext {
  goal?: string
  tasks: readonly { id: string; title: string }[]
  workstreams: readonly { id: string; name: string }[]
  research: readonly { id: string; conversation?: { barkId?: string } }[]
}

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim()
const clean = (line: string) =>
  line
    .trim()
    .replace(/^(?:[-*+]|\d+[.)])\s+/, '')
    .replace(/\*\*|__/g, '')
    .trim()
const GOAL = /^goal\s*[:：]\s*(.+)$/i
const PLAN = /^plan\s*[:：]\s*(.*)$/i
const TASK = /^(?:task|todo)\s*[:：]\s*(.+)$/i
const isMarker = (l: string) => GOAL.test(l) || PLAN.test(l) || TASK.test(l)

function fnv(s: string) {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(36)
}

type Found = { messageId: string; quote: string }

export function distillConversation(b: BarkRecord, ctx: DistillContext): DistillProposal[] {
  let goal: { text: string; ev: Found } | undefined
  let plan: { text: string; ev: Found } | undefined
  const tasks: { title: string; ev: Found }[] = []
  for (const m of b.thread) {
    if (m.author === 'system') continue
    const lines = m.text.split('\n')
    for (let i = 0; i < lines.length; i++) {
      const line = clean(lines[i]!)
      const ev = { messageId: m.id, quote: line.slice(0, 500) }
      const g = GOAL.exec(line)
      if (g) {
        goal = { text: g[1]!.trim(), ev }
        continue
      }
      const p = PLAN.exec(line)
      if (p) {
        const body = p[1]!.trim() ? [p[1]!.trim()] : []
        // The plan's own bullets follow until a blank line or the next outcome line.
        while (i + 1 < lines.length && lines[i + 1]!.trim() && !isMarker(clean(lines[i + 1]!)))
          body.push(lines[++i]!.trim())
        if (body.length) plan = { text: body.join('\n'), ev }
        continue
      }
      const t = TASK.exec(line)
      if (t) tasks.push({ title: t[1]!.trim().slice(0, 200), ev })
    }
  }

  const ws =
    ctx.workstreams.find((w) => w.id === b.hints.workstreamId) ??
    (b.title ? ctx.workstreams.find((w) => norm(w.name) === norm(b.title!)) : undefined)
  const research = ctx.research.filter((r) => r.conversation?.barkId === b.id).map((r) => r.id)
  const out: DistillProposal[] = []
  const card = (title: string, change: DistillChange, evidence: Found[]) =>
    out.push({
      id: `d:${change.op.replace('.', '-')}:${fnv(`${b.id}|${JSON.stringify(change)}`)}`,
      projectId: b.projectId,
      barkId: b.id,
      title: title.slice(0, 200),
      change,
      evidence,
      ...(research.length ? { research: research.slice(0, 10) } : {}),
      status: 'open',
    })

  if (goal && norm(goal.text) !== norm(ctx.goal ?? ''))
    card('Update the project goal', { op: 'goal.set', goal: goal.text.slice(0, 4000) }, [goal.ev])
  if (plan) {
    const name = ws?.name ?? b.title ?? 'Plan from conversation'
    card(
      `Plan for ${name}`,
      { op: 'plan.brief', workstreamId: ws?.id ?? null, name, brief: plan.text.slice(0, 20_000) },
      [plan.ev],
    )
  }
  const known = new Set(ctx.tasks.map((t) => norm(t.title)))
  for (const t of tasks) {
    if (known.has(norm(t.title))) continue
    known.add(norm(t.title))
    card(t.title, { op: 'task.create', title: t.title, ...(ws ? { workstreamId: ws.id } : {}) }, [
      t.ev,
    ])
  }
  for (const p of b.proposals) {
    if (p.status !== 'open') continue
    const msg = b.thread.find((m) => m.proposalId === p.id)
    card(p.title, { op: 'bark.confirm', proposalId: p.id, actions: p.actions.length }, [
      { messageId: msg?.id ?? p.id, quote: (msg?.text ?? p.note ?? p.title).slice(0, 500) },
    ])
  }
  return out
}

/** The encrypted writes an accepted card performs (private work API + the bark store). */
export interface DistillWriters {
  setGoal(projectId: string, goal: string): Promise<unknown>
  updateBrief(workstreamId: string, brief: string): Promise<unknown>
  createWorkstream(i: { projectId: string; name: string; brief: string }): Promise<unknown>
  createTask(i: {
    projectId: string
    title: string
    workstreamId?: string
    description?: string
    acceptance?: string[]
  }): Promise<unknown>
  setPriority(taskId: string, priority: number): Promise<unknown>
  /** Confirms the agent's proposal through its signed digest (bark.control confirm). */
  confirmProposal(barkId: string, proposalId: string): Promise<unknown>
}

export async function applyDistill(p: DistillProposal, w: DistillWriters): Promise<void> {
  if (p.status !== 'open') throw new Error('This proposal was already reviewed')
  const c = p.change
  switch (c.op) {
    case 'goal.set':
      await w.setGoal(p.projectId, c.goal)
      return
    case 'plan.brief':
      if (c.workstreamId) await w.updateBrief(c.workstreamId, c.brief)
      else await w.createWorkstream({ projectId: p.projectId, name: c.name, brief: c.brief })
      return
    case 'task.create':
      await w.createTask({
        projectId: p.projectId,
        title: c.title,
        ...(c.workstreamId ? { workstreamId: c.workstreamId } : {}),
        ...(c.description ? { description: c.description } : {}),
        ...(c.acceptance?.length ? { acceptance: c.acceptance } : {}),
      })
      return
    case 'task.priority':
      await w.setPriority(c.taskId, c.priority)
      return
    case 'bark.confirm':
      await w.confirmProposal(p.barkId, c.proposalId)
      return
  }
}
